// database/repositories/metricLogRepository.ts
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../client';
import { Metric, MetricLog, MediaMetricLog } from '../types';

export interface PopulatedNumericMetric extends Metric {
  windowStart?: EpochTimeStamp;
  windowEnd?: EpochTimeStamp;
  entryType: 'number';
  entries?: Array<MetricLog>;
}

export interface PopulatedMediaMetric extends Metric {
  entryType: 'media';
  entries?: Array<MediaMetricLog>;
}

export type PopulatedMetric = PopulatedNumericMetric | PopulatedMediaMetric;

export const metricLogRepository = {
  async getMetric(metricId: string): Promise<Metric | undefined> {
    const record = await db.metrics.get(metricId);
    return record;
  },

  /**
   * Populate a metric with its associated logs to a default or within a specified time window.
   */

  async getRecentMetricLogs(metricId: string): Promise<PopulatedMetric> {
    const metric = await db.metrics.get(metricId);
    if (!metric) {
      throw new Error(`Metric with ID ${metricId} not found.`);
    }

    if (metric.type === 'media') {
      const logs = await db.mediaMetricLogs
        .where('metricId')
        .equals(metricId)
        .limit(10)
        .toArray();

      return {
        ...metric,
        entryType: 'media',
        entries: logs,
      };
    } else {
      const logs = await db.metricLogs
        .where('metricId')
        .equals(metricId)
        .limit(50)
        .toArray();

      return {
        ...metric,
        entryType: 'number',
        entries: logs,
      };
    }
  },

  async createLog(metricId: string, value: number): Promise<string> {
    const metric = await db.metrics.get(metricId);

    if (!metric || metric.type === 'media') {
      throw new Error(`Media types not supported yet!`);
    }

    const addResult = await db.metricLogs.add({
      metricId,
      timestamp: Date.now(),
      value,
    });

    if (!addResult) {
      throw new Error(`Adding failed! ${metricId}, ${value}`);
    }

    return addResult;
  },

  async saveMetricWithUndo(metricId: string, value: number) {
    const logId = crypto.randomUUID();
    await db.metricLogs.add({
      id: logId,
      metricId,
      timestamp: Date.now(),
      value,
    });
    return { id: logId };
  },
};

export function useRecentMetric(metricId: string) {
  return useLiveQuery(
    () => metricLogRepository.getRecentMetricLogs(metricId),
    [metricId]
  );
}

export function useMetric(metricId: string) {
  return useLiveQuery(
    () => metricLogRepository.getMetric(metricId),
    [metricId]
  );
}
